import { type KeyboardEventHandler, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { routes } from '@shared/api/routes';
import { Icon, UiButton, UiText, UITooltip } from '@shared/ui';

import cls from './ApiTokenSettings.module.css';
import { useApiTokenSettings } from '../../model/useApiTokenSettings';

import type { Profile } from '@entities/profile';

type ApiTokenSettingsProps = {
    profile: Profile;
    onPaySubscriptionClick?: ({ removeProlong }: { removeProlong: boolean }) => void;
};

export const ApiTokenSettings = ({ profile, onPaySubscriptionClick }: ApiTokenSettingsProps) => {
    const { t } = useTranslation();
    const tokenRef = useRef<HTMLSpanElement>(null);
    const { apiToken, canUpdate, updateToken, copyToken, isPending } = useApiTokenSettings(profile);

    const selectToken = () => {
        if (!tokenRef.current) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(tokenRef.current);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
    };

    const handleTokenKeyDown: KeyboardEventHandler<HTMLSpanElement> = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            selectToken();
        }
    };

    const handleApiTokenButtonClick = () => {
        if (canUpdate) {
            updateToken();
            return;
        }

        onPaySubscriptionClick?.({ removeProlong: true });
    };

    return (
        <section className={cls.settingsSection}>
            <div className={cls.settingsLabelWithHelp}>
                <UiText tag="h3" variant="subHeading" weight="bold">
                    {t('profile.page.settings.api.title')}
                </UiText>
                <UITooltip
                    label={
                        <>
                            <UiText variant="mediumPlain">
                                {t('profile.page.settings.api.help')}{' '}
                            </UiText>
                            <a
                                href={routes.profilePublicApiDocs()}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t('profile.page.settings.api.help.link')}
                            </a>
                        </>
                    }
                >
                    <UiButton
                        type="button"
                        variant="icon"
                        btnColor="ghost"
                        className={cls.helpButton}
                        startIcon={<Icon iconName="question" />}
                        aria-label={t('profile.page.settings.showHelp')}
                    />
                </UITooltip>
            </div>
            {apiToken && (
                <div className={cls.apiTokenRow}>
                    <span
                        role="button"
                        tabIndex={0}
                        className={cls.apiTokenValue}
                        onClick={selectToken}
                        onKeyDown={handleTokenKeyDown}
                    >
                        <span ref={tokenRef}>{apiToken}</span>
                    </span>
                    <UiButton
                        type="button"
                        variant="icon"
                        btnColor="ghost"
                        className={cls.apiTokenCopyButton}
                        startIcon={<Icon iconName="copy" width={18} height={18} />}
                        aria-label={t('profile.page.settings.api.copy')}
                        onClick={copyToken}
                    />
                </div>
            )}
            <UiButton
                type="button"
                variant="text"
                className={cls.settingsLinkButton}
                btnText={
                    apiToken
                        ? t('profile.page.settings.api.generateNewToken')
                        : t('profile.page.settings.api.generateToken')
                }
                disabled={isPending}
                onClick={handleApiTokenButtonClick}
            />
        </section>
    );
};
